import { getFileUrl } from '@/clients/http'
import { Award, ExternalLink, Pencil, Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useBrandsTable } from '@/hooks/features/brands'
import { useDataTable } from '@/hooks/ui'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'

import {
  ActionDropdown,
  ConfirmDialog,
  PageHeader,
  StatsCards,
  StatusBadge,
} from '@/components/atoms'

import { BrandModel } from '@/types'

import { BrandDialog } from './BrandDialog'
import { BrandsFilters } from './BrandsFilters'

type TableColumns = 'logo' | 'name' | 'status' | 'products_count' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'logo',
  'name',
  'status',
  'products_count',
  'actions',
]

export const BrandsTable = () => {
  const {
    loading,
    data,
    pagination,
    formMethods,
    stats,
    mode,
    brandDialogOpen,
    editBrand,
    deleteDialogOpen,
    brandToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateBrand,
    handleUpdateBrand,
    toggleStatus,
  } = useBrandsTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    BrandModel.Brand,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'logo',
        columnLabel: () => 'Image',
        cell: (data) => (
          <div className="flex items-center">
            {data?.logo?.path ? (
              <img
                src={getFileUrl(data?.logo?.path)}
                alt={data?.name}
                className="size-10 rounded-md border border-border-subtle object-cover"
              />
            ) : (
              <div className="flex size-10 items-center justify-center rounded-md border border-border-subtle bg-muted">
                <Award className="size-4 text-muted-foreground" aria-hidden="true" />
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'name',
        columnLabel: () => 'Name',
        cell: (data) => <span className="font-medium text-ink">{data?.name}</span>,
        hasSorting: true,
      },
      {
        accessorKey: 'products_count',
        columnLabel: () => 'Products',
        cell: (data) => <span className="tabular-nums text-ink">{data?.products_count}</span>,
      },
      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <StatusBadge
              type="product"
              status={data?.status === 'active' ? 'active' : 'inactive'}
            />
            <Switch
              checked={data?.status === 'active'}
              onCheckedChange={() => toggleStatus(data)}
            />
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'View',
                  icon: Award,
                  onClick: () => openViewDialog(data),
                },
                {
                  label: 'Edit',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Brands"
        description="Brands and manufacturers you sell."
        onAddClick={openCreateDialog}
        addButtonText="Add brand"
      />

      <StatsCards
        stats={[
          {
            title: 'Total brands',
            value: stats.totalBrands,
            icon: Award,
          },
          {
            title: 'Active brands',
            value: stats.activeBrands,
          },
          {
            title: 'Inactive brands',
            value: stats.inactiveBrands,
          },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <FormProvider {...formMethods}>
                <BrandsFilters />
              </FormProvider>
            </div>
            <ColumnVisibilityDropdown
              columns={columns}
              onColumnVisibilityChange={changeColumnVisibility}
              disabledColumns={disabledColumns}
            />
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No brands found"
              btnText="Add brand"
              openCreateDialog={openCreateDialog}
            />
          ) : (
            <>
              <DataTable columns={columns as any} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <BrandDialog
        open={brandDialogOpen}
        mode={mode}
        editBrand={editBrand}
        closeDialog={closeDialog}
        onCreate={handleCreateBrand}
        onUpdate={handleUpdateBrand}
        loading={createLoading || updateLoading}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete brand?"
        description={`This will permanently delete the brand "${brandToDelete?.name}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
